import { supabase } from '../lib/supabase';

export type DeadlineUrgency = 'none' | 'normal' | 'warning' | 'critical' | 'expired';

export interface TournamentMatch {
  id: string;
  tournament_id: string;
  round_number: number;
  match_number: number;
  participant1_id: string | null;
  participant2_id: string | null;
  participant1_entry_id: string | null;
  participant2_entry_id: string | null;
  participant1_recording_id: string | null;
  participant2_recording_id: string | null;
  winner_id: string | null;
  match_status: 'pending' | 'one_submitted' | 'both_submitted' | 'scoring' | 'completed' | 'forfeited';
  next_match_id: string | null;
  round_start_time: string | null;
  submission_deadline: string | null;
  forfeit_reason: 'manual' | 'timeout_one_submission' | 'timeout_both_no_submission' | null;
  participant1_score: number | null;
  participant2_score: number | null;
  completed_at: string | null;
  created_at: string;
}

export interface MatchWithDetails extends TournamentMatch {
  participant1?: {
    id: string;
    username: string;
    avatar_url?: string;
  };
  participant2?: {
    id: string;
    username: string;
    avatar_url?: string;
  };
  participant1_entry?: {
    id: string;
    score: number | null;
    status: string;
    bracket_seed?: number | null;
  };
  participant2_entry?: {
    id: string;
    score: number | null;
    status: string;
    bracket_seed?: number | null;
  };
}

export interface BracketConfig {
  totalRounds: number;
  bracketSize: number;
  seedingMethod: string;
}

export interface BracketData {
  matches: MatchWithDetails[];
  totalRounds: number;
  bracketSize: number;
}

export class MatchProgressionService {
  static async getTournamentBracket(tournamentId: string): Promise<BracketData> {
    const { data: config, error: configError } = await supabase
      .from('bracket_progression')
      .select('total_rounds, bracket_size')
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    if (configError || !config) {
      throw new Error('Bracket not generated yet');
    }

    const { data: matches, error: matchesError } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        participant1:profiles!tournament_matches_participant1_id_profiles_fkey(id, username, avatar_url),
        participant2:profiles!tournament_matches_participant2_id_profiles_fkey(id, username, avatar_url),
        participant1_entry:tournament_entries!tournament_matches_participant1_entry_id_fkey(id, score, status, bracket_seed),
        participant2_entry:tournament_entries!tournament_matches_participant2_entry_id_fkey(id, score, status, bracket_seed)
      `)
      .eq('tournament_id', tournamentId)
      .order('round_number', { ascending: true })
      .order('match_number', { ascending: true });

    if (matchesError) {
      console.error('Match query error:', matchesError);
      throw new Error('Failed to load tournament matches');
    }

    return {
      matches: matches as any,
      totalRounds: config.total_rounds,
      bracketSize: config.bracket_size
    };
  }

  static async getUserTournamentEntry(tournamentId: string, userId: string): Promise<{
    id: string;
    bracket_seed: number | null;
    current_round: number;
    eliminated_at: string | null;
    score: number | null;
    status: string;
    is_disqualified: boolean;
    finalist_placement: number | null;
  } | null> {
    const { data, error } = await supabase
      .from('tournament_entries')
      .select('id, bracket_seed, current_round, eliminated_at, score, status, is_disqualified, finalist_placement')
      .eq('tournament_id', tournamentId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data) return null;
    return data as any;
  }

  static async getBracketConfig(tournamentId: string): Promise<BracketConfig | null> {
    const { data, error } = await supabase
      .from('bracket_progression')
      .select('total_rounds, bracket_size, seeding_method')
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    if (error || !data) return null;

    return {
      totalRounds: data.total_rounds,
      bracketSize: data.bracket_size,
      seedingMethod: data.seeding_method,
    };
  }

  static async getMatchById(matchId: string): Promise<MatchWithDetails | null> {
    const { data: match, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        participant1:profiles!tournament_matches_participant1_id_profiles_fkey(id, username, avatar_url),
        participant2:profiles!tournament_matches_participant2_id_profiles_fkey(id, username, avatar_url),
        participant1_entry:tournament_entries!tournament_matches_participant1_entry_id_fkey(id, score, status, bracket_seed),
        participant2_entry:tournament_entries!tournament_matches_participant2_entry_id_fkey(id, score, status, bracket_seed)
      `)
      .eq('id', matchId)
      .maybeSingle();

    if (error || !match) {
      return null;
    }

    return match as any;
  }

  static async getUserMatches(tournamentId: string, userId: string): Promise<MatchWithDetails[]> {
    const { data: matches, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        participant1:profiles!tournament_matches_participant1_id_profiles_fkey(id, username, avatar_url),
        participant2:profiles!tournament_matches_participant2_id_profiles_fkey(id, username, avatar_url),
        participant1_entry:tournament_entries!tournament_matches_participant1_entry_id_fkey(id, score, status, bracket_seed),
        participant2_entry:tournament_entries!tournament_matches_participant2_entry_id_fkey(id, score, status, bracket_seed)
      `)
      .eq('tournament_id', tournamentId)
      .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
      .order('round_number', { ascending: true });

    if (error) {
      throw new Error('Failed to load user matches');
    }

    const seen = new Set<string>();
    const deduped = (matches ?? []).filter(m => {
      const key = `${m.round_number}-${m.match_number}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return deduped as any;
  }

  static async getCurrentMatch(tournamentId: string, userId: string): Promise<MatchWithDetails | null> {
    const { data: matches, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        participant1:profiles!tournament_matches_participant1_id_profiles_fkey(id, username, avatar_url),
        participant2:profiles!tournament_matches_participant2_id_profiles_fkey(id, username, avatar_url),
        participant1_entry:tournament_entries!tournament_matches_participant1_entry_id_fkey(id, score, status, bracket_seed),
        participant2_entry:tournament_entries!tournament_matches_participant2_entry_id_fkey(id, score, status, bracket_seed)
      `)
      .eq('tournament_id', tournamentId)
      .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
      .in('match_status', ['pending', 'one_submitted', 'both_submitted', 'scoring'])
      .order('round_number', { ascending: true })
      .limit(1);

    if (error || !matches || matches.length === 0) {
      return null;
    }

    return matches[0] as any;
  }

  static getDeadlineUrgency(match: MatchWithDetails): { urgency: DeadlineUrgency; remainingMs: number } {
    if (!match.submission_deadline) {
      return { urgency: 'none', remainingMs: 0 };
    }

    const now = Date.now();
    const deadline = new Date(match.submission_deadline).getTime();
    const remainingMs = deadline - now;

    if (remainingMs <= 0) return { urgency: 'expired', remainingMs: 0 };
    if (remainingMs <= 60 * 60 * 1000) return { urgency: 'critical', remainingMs };
    if (remainingMs <= 6 * 60 * 60 * 1000) return { urgency: 'warning', remainingMs };
    return { urgency: 'normal', remainingMs };
  }

  static getMatchStatusDisplay(match: MatchWithDetails, currentUserId?: string): {
    status: string;
    canSubmit: boolean;
    showScores: boolean;
    userSubmitted: boolean;
    opponentSubmitted: boolean;
    forfeitReason: string | null;
  } {
    const isParticipant1 = match.participant1_id === currentUserId;
    const isParticipant2 = match.participant2_id === currentUserId;

    const participant1Submitted = !!match.participant1_recording_id;
    const participant2Submitted = !!match.participant2_recording_id;

    const userSubmitted = (isParticipant1 && participant1Submitted) || (isParticipant2 && participant2Submitted);
    const opponentSubmitted = (isParticipant1 && participant2Submitted) || (isParticipant2 && participant1Submitted);

    const bothSubmitted = participant1Submitted && participant2Submitted;

    const participant1Scored = match.participant1_entry?.status === 'evaluated';
    const participant2Scored = match.participant2_entry?.status === 'evaluated';
    const bothScored = participant1Scored && participant2Scored;

    const showScores = bothSubmitted && bothScored && match.match_status === 'completed';

    let status = 'Pending';
    let canSubmit = false;

    switch (match.match_status) {
      case 'pending':
        if (!participant1Submitted && !participant2Submitted) {
          status = 'Waiting for submissions';
          canSubmit = !userSubmitted;
        } else if (participant1Submitted && !participant2Submitted) {
          status = isParticipant1 ? 'Waiting for opponent' : 'Your turn to submit';
          canSubmit = !userSubmitted;
        } else if (!participant1Submitted && participant2Submitted) {
          status = isParticipant2 ? 'Waiting for opponent' : 'Your turn to submit';
          canSubmit = !userSubmitted;
        }
        break;

      case 'one_submitted':
        if (userSubmitted) {
          status = 'Waiting for opponent';
        } else {
          status = 'Your turn to submit';
          canSubmit = true;
        }
        break;

      case 'both_submitted':
      case 'scoring':
        status = 'Scoring in progress';
        break;

      case 'completed':
        status = 'Completed';
        break;

      case 'forfeited':
        if (match.forfeit_reason === 'timeout_one_submission') {
          status = 'Timed Out';
        } else if (match.forfeit_reason === 'timeout_both_no_submission') {
          status = 'Double Timeout';
        } else {
          status = 'Forfeited';
        }
        break;
    }

    return {
      status,
      canSubmit,
      showScores,
      userSubmitted,
      opponentSubmitted,
      forfeitReason: match.forfeit_reason || null
    };
  }

  static async submitMatchEntry(
    matchId: string,
    entryId: string,
    recordingId: string,
    userId: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const match = await this.getMatchById(matchId);
      if (!match) {
        return { success: false, message: 'Match not found' };
      }

      const isParticipant1 = match.participant1_id === userId;
      const isParticipant2 = match.participant2_id === userId;

      if (!isParticipant1 && !isParticipant2) {
        return { success: false, message: 'Not a participant in this match' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-tournament-entry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            tournament_entry_id: entryId,
            recording_id: recordingId,
            user_id: userId,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return { success: false, message: result.error || 'Failed to submit entry' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error submitting match entry:', error);
      return { success: false, message: 'Unexpected error occurred' };
    }
  }

  static async forfeitMatch(matchId: string, userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const match = await this.getMatchById(matchId);
      if (!match) {
        return { success: false, message: 'Match not found' };
      }

      const isParticipant1 = match.participant1_id === userId;
      const isParticipant2 = match.participant2_id === userId;

      if (!isParticipant1 && !isParticipant2) {
        return { success: false, message: 'Not a participant in this match' };
      }

      const winnerId = isParticipant1 ? match.participant2_id : match.participant1_id;
      const loserEntryId = isParticipant1 ? match.participant1_entry_id : match.participant2_entry_id;

      const { error: updateError } = await supabase
        .from('tournament_matches')
        .update({
          winner_id: winnerId,
          match_status: 'forfeited',
          forfeit_reason: 'manual',
          completed_at: new Date().toISOString(),
        })
        .eq('id', matchId);

      if (updateError) {
        return { success: false, message: 'Failed to forfeit match' };
      }

      if (loserEntryId) {
        await supabase
          .from('tournament_entries')
          .update({
            eliminated_at: new Date().toISOString(),
          })
          .eq('id', loserEntryId);
      }

      return { success: true };
    } catch (error) {
      console.error('Error forfeiting match:', error);
      return { success: false, message: 'Unexpected error occurred' };
    }
  }

  static async resetMatch(matchId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const match = await this.getMatchById(matchId);
      if (!match) {
        return { success: false, message: 'Match not found' };
      }

      const { data: tournament, error: tournamentError } = await supabase
        .from('tournaments')
        .select('round_duration_minutes')
        .eq('id', match.tournament_id)
        .maybeSingle();

      if (tournamentError || !tournament) {
        return { success: false, message: 'Failed to load tournament data' };
      }

      const now = new Date();
      const newDeadline = tournament.round_duration_minutes
        ? new Date(now.getTime() + tournament.round_duration_minutes * 60 * 1000).toISOString()
        : null;

      const { error: matchError } = await supabase
        .from('tournament_matches')
        .update({
          match_status: 'pending',
          participant1_recording_id: null,
          participant2_recording_id: null,
          winner_id: null,
          forfeit_reason: null,
          completed_at: null,
          round_start_time: now.toISOString(),
          submission_deadline: newDeadline,
        })
        .eq('id', matchId);

      if (matchError) {
        return { success: false, message: 'Failed to reset match' };
      }

      const entriesToRestore: string[] = [];
      if (match.participant1_entry_id) entriesToRestore.push(match.participant1_entry_id);
      if (match.participant2_entry_id) entriesToRestore.push(match.participant2_entry_id);

      if (entriesToRestore.length > 0) {
        await supabase
          .from('tournament_entries')
          .update({ eliminated_at: null, status: 'active' })
          .in('id', entriesToRestore);
      }

      return { success: true };
    } catch (error) {
      console.error('Error resetting match:', error);
      return { success: false, message: 'Unexpected error occurred' };
    }
  }

  static subscribeToTournamentMatches(
    tournamentId: string,
    callback: (payload: any) => void
  ): any {
    return supabase
      .channel(`tournament_matches_${tournamentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournament_matches',
          filter: `tournament_id=eq.${tournamentId}`,
        },
        callback
      )
      .subscribe();
  }

  static subscribeToMatch(
    matchId: string,
    callback: (payload: any) => void
  ): any {
    return supabase
      .channel(`tournament_match_${matchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tournament_matches',
          filter: `id=eq.${matchId}`,
        },
        callback
      )
      .subscribe();
  }
}
